import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, KanbanSquare, Inbox, CalendarCheck, Users, Settings, LogOut, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, testid: "nav-pipeline" },
  { to: "/enquiries", label: "Enquiries", icon: Inbox, testid: "nav-enquiries" },
  { to: "/today", label: "Today's Follow-ups", icon: CalendarCheck, testid: "nav-today" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

const NavItems = ({ onNavigate }) => (
  <nav className="flex flex-col gap-1 p-3">
    {links.map((l) => {
      const Icon = l.icon;
      return (
        <NavLink
          key={l.to}
          to={l.to}
          end={l.end}
          data-testid={l.testid}
          onClick={onNavigate}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
              isActive ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-slate-100"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <span className="h-5 w-0.5 rounded-full transition-colors" style={{ backgroundColor: isActive ? "hsl(var(--brand-accent))" : "transparent" }} />
              <Icon className="h-[18px] w-[18px]" />
              {l.label}
            </>
          )}
        </NavLink>
      );
    })}
  </nav>
);

const UserFooter = ({ user, logout }) => (
  <div className="border-t border-slate-800 p-4">
    <div className="mb-3">
      <p className="truncate text-sm font-semibold text-slate-200" data-testid="sidebar-user">{user?.name || "Team member"}</p>
      <p className="truncate text-[11px] text-slate-500">{user?.email}</p>
    </div>
    <button onClick={logout} data-testid="logout-button" className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-400 transition-colors hover:bg-white/5 hover:text-slate-100">
      <LogOut className="h-[18px] w-[18px]" /> Sign out
    </button>
  </div>
);

export const Sidebar = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 z-40 hidden w-64 flex-col justify-between border-r border-slate-800 bg-slate-900 text-slate-100 lg:flex">
        <div>
          <div className="flex h-16 items-center border-b border-slate-800 px-5"><Logo /></div>
          <NavItems />
        </div>
        <UserFooter user={user} logout={logout} />
      </aside>

      {/* Mobile top bar */}
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b border-slate-800 bg-slate-900 px-4 text-slate-100 lg:hidden">
        <Logo />
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu" className="grid h-10 w-10 place-items-center rounded-lg text-slate-300 hover:bg-white/10">
          <Menu className="h-6 w-6" />
        </button>
      </header>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-50 lg:hidden ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
        <div className={`absolute inset-0 bg-slate-950/60 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} />
        <aside className={`absolute left-0 top-0 bottom-0 flex w-72 max-w-[82%] flex-col justify-between border-r border-slate-800 bg-slate-900 text-slate-100 shadow-2xl transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"}`} data-testid="mobile-drawer">
          <div>
            <div className="flex h-14 items-center justify-between border-b border-slate-800 px-4">
              <Logo />
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu" className="grid h-9 w-9 place-items-center rounded-lg text-slate-300 hover:bg-white/10">
                <X className="h-5 w-5" />
              </button>
            </div>
            <NavItems onNavigate={() => setOpen(false)} />
          </div>
          <UserFooter user={user} logout={logout} />
        </aside>
      </div>
    </>
  );
};
